import type { ReactNode } from 'react';
import type { RunScorecard, ScorecardRegression } from '@aiai/shared';
import { formatCost, formatSeconds } from '../lib/format';

function Metric({
  label,
  value,
  sub,
}: {
  label: string;
  value: ReactNode;
  sub?: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-medium uppercase tracking-wide text-ink-faint">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-ink">
        {value}
        {sub && <span className="ml-1 text-xs font-normal text-ink-faint">{sub}</span>}
      </dd>
    </div>
  );
}

function RegressionFlag({ r }: { r: ScorecardRegression }) {
  const color =
    r.severity === 'alert'
      ? 'bg-danger-soft border-danger/40 text-danger'
      : 'bg-warning-soft border-warning/40 text-warning';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-[3px] border-[1.5px] px-1.5 py-0.5 text-[0.5625rem] font-display font-bold uppercase tracking-[0.1em] ${color}`}
      title={`${r.label}: ${r.value} vs baseline ${r.baseline.toFixed(2)} (${r.multiplier}×)`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {r.label} {r.multiplier}×
    </span>
  );
}

export function ScorecardPanel({ scorecard }: { scorecard: RunScorecard }) {
  const linesChanged = scorecard.linesAdded + scorecard.linesRemoved;
  const hasRegressions = scorecard.regressions.length > 0;

  return (
    <div className="flex flex-col gap-3">
      {hasRegressions && (
        <div className="flex flex-wrap gap-1.5">
          {scorecard.regressions.map((r) => (
            <RegressionFlag key={r.metric} r={r} />
          ))}
        </div>
      )}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
        <Metric
          label="Loops"
          value={scorecard.loopIterations}
          sub={scorecard.loopIterations === 1 ? 'iteration' : 'iterations'}
        />
        <Metric
          label="Files changed"
          value={scorecard.filesChanged}
        />
        <Metric
          label="Lines changed"
          value={linesChanged}
          sub={`+${scorecard.linesAdded} −${scorecard.linesRemoved}`}
        />
        <Metric
          label="Tests added"
          value={scorecard.testsAddedCount}
        />
        <Metric
          label="Cost"
          value={formatCost(scorecard.totalCostUsd)}
        />
        <Metric
          label="Duration"
          value={formatSeconds(scorecard.durationSeconds)}
        />
      </dl>
    </div>
  );
}
